/**
 * useModels Hook
 * Module 07 - Dashboard UI
 * Section 08 - State Management
 * 
 * Hook for managing models data and operations with React Query
 * Enhanced with centralized query keys and optimized caching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { modelsService } from '../services';
import { useToast } from './useToast';
import { Model } from '../types';
import { queryKeys } from '../lib/queryClient';

type ApiError = AxiosError<{ message?: string }>;

/**
 * useModels provides models data and operations
 * Uses React Query for caching and automatic refetching
 */
export const useModels = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Fetch models
  const {
    data: models = [],
    isLoading,
    error,
    refetch,
  } = useQuery<Model[]>({
    queryKey: queryKeys.models.all,
    queryFn: () => modelsService.getModels(),
    staleTime: 10000, // Consider data fresh for 10 seconds
    // Poll only while uploads are processing; otherwise rely on manual refresh
    refetchInterval: (query) => {
      const raw = (query as any)?.state?.data ?? (query as any)?.data ?? query;
      const list: Model[] =
        Array.isArray(raw)
          ? raw
          : Array.isArray((raw as any)?.models)
            ? (raw as any).models
            : [];

      const hasProcessing = list.some(
        (m) => m.status === 'processing' || m.status === 'uploaded'
      );
      return hasProcessing ? 10000 : false; // 10s while processing, off otherwise
    },
  });

  // Rename model mutation — optimistic update with rollback on error
  const renameModelMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      modelsService.renameModel(id, name),
    onMutate: async ({ id, name }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.models.all });
      const previousModels = queryClient.getQueryData<Model[]>(queryKeys.models.all);
      queryClient.setQueryData<Model[]>(
        queryKeys.models.all,
        (old) => old?.map((m) => (m.id === id ? { ...m, name } : m)) ?? []
      );
      return { previousModels };
    },
    onError: (error: ApiError, _vars, context) => {
      // Rollback optimistic update on failure
      if (context?.previousModels) {
        queryClient.setQueryData(queryKeys.models.all, context.previousModels);
      }
      showToast('error', error.response?.data?.message ?? 'Failed to rename model');
    },
    onSuccess: () => {
      showToast('success', 'Model renamed successfully');
    },
    onSettled: () => {
      // Re-sync with server after rename attempt
      queryClient.invalidateQueries({ queryKey: queryKeys.models.all });
    },
  });

  // Delete model mutation
  const deleteModelMutation = useMutation({
    mutationFn: (id: string) => modelsService.deleteModel(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.models.all });

      // Snapshot the previous value
      const previousModels = queryClient.getQueryData<Model[]>(queryKeys.models.all);

      // Optimistically update to remove the model
      queryClient.setQueryData<Model[]>(
        queryKeys.models.all,
        (old) => old?.filter((model) => model.id !== id) ?? []
      );

      // Return context with previous data
      return { previousModels };
    },
    onError: (error: ApiError, _id, context) => {
      // Rollback on error
      if (context?.previousModels) {
        queryClient.setQueryData(queryKeys.models.all, context.previousModels);
      }
      showToast(
        'error',
        error.response?.data?.message || 'Failed to delete model'
      );
    },
    onSuccess: () => {
      showToast('success', 'Model deleted successfully');
    },
    onSettled: () => {
      // Refetch to ensure we're in sync with server
      queryClient.invalidateQueries({ queryKey: queryKeys.models.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.models.storageUsage });
    },
  });

  // Get single model with caching
  const getModel = async (id: string): Promise<Model> => {
    // Check if we already have this model in the list
    const cachedModel = models.find((m) => m.id === id);
    if (cachedModel) {
      return cachedModel;
    }

    // Otherwise fetch from server
    return modelsService.getModel(id);
  };

  // Get storage usage
  const { data: storageUsage } = useQuery({
    queryKey: queryKeys.models.storageUsage,
    queryFn: () => modelsService.getStorageUsage(),
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  return {
    // Data
    models,
    isLoading,
    error,
    storageUsage,

    // Operations
    deleteModel: deleteModelMutation.mutateAsync,
    renameModel: (id: string, name: string) => renameModelMutation.mutateAsync({ id, name }),
    getModel,
    refetch,

    // Loading states
    isDeleting: deleteModelMutation.isPending,
    isRenaming: renameModelMutation.isPending,

    // Computed values
    readyModels: models.filter((m) => m.status === 'ready'),
    processingModels: models.filter((m) => m.status === 'processing'),
    failedModels: models.filter((m) => m.status === 'failed'),

    // Statistics
    totalModels: models.length,
    totalSize: models.reduce((acc, m) => acc + m.originalFileSize, 0),
  };
};
